import React from "react";

interface RadioOption<T = string> {
  value: T;
  label: string | React.ReactNode;
  description?: string | React.ReactNode;
  action?: string | React.ReactNode;
  hasAction?: boolean;
  disabled?: boolean;
  testId?: string;
}

interface RadioGroupProps<T = string> {
  name: string;
  value?: T;
  onChange: (value: T) => void;
  options: RadioOption<T>[];
  orientation?: "horizontal" | "vertical";
  className?: string;
  variant?: "default" | "cards";
}

export default function RadioGroup<T = string>({
  name,
  value,
  onChange,
  options,
  orientation = "vertical",
  className = "",
  variant = "default",
}: RadioGroupProps<T>) {
  const containerClasses =
    variant === "cards"
      ? `grid ${orientation === "horizontal" ? "grid-flow-col auto-cols-fr" : "grid-cols-1"} gap-3 ${className}`
      : `space-${orientation === "horizontal" ? "x" : "y"}-3 ${
          orientation === "horizontal" ? "flex flex-wrap" : ""
        } ${className}`;

  return (
    <div className={containerClasses} role="radiogroup">
      {options.map((option, index) => {
        const isSelected = option.value === value;
        const optionId = `${name}-${index}`;

        if (variant === "cards") {
          return (
            <label
              key={
                typeof option.value === "object"
                  ? JSON.stringify(option.value)
                  : String(option.value)
              }
              data-testid={option.testId}
              className={`relative cursor-pointer rounded-2xl border p-4 pr-12 bg-white border-slate-200 dark:bg-slate-800/40 dark:border-transparent ${
                isSelected
                  ? "shadow-[inset_0_0_0_2px_rgba(129,140,248,0.95)]"
                  : "shadow-[inset_0_0_0_1px_rgba(148,163,184,0.22)] hover:bg-slate-50 dark:hover:bg-slate-800/50"
              } ${option.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <input
                id={optionId}
                name={name}
                type="radio"
                value={String(option.value)}
                checked={isSelected}
                disabled={option.disabled}
                onChange={() => !option.disabled && onChange(option.value)}
                className="sr-only"
              />
              <div className="flex items-start">
                <div className="flex-1">
                  <div
                    className={`font-medium text-slate-900 dark:text-slate-100${
                      option.description ? " mb-1" : ""
                    }`}
                  >
                    {option.label}
                  </div>
                  {option.description && (
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      {option.description}
                    </div>
                  )}
                  {option.hasAction && <>{option.action}</>}
                </div>
              </div>
              {isSelected && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <svg
                    className="size-5 text-indigo-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </label>
          );
        }

        // Default variant - custom styled radio buttons
        return (
          <label
            key={
              typeof option.value === "object"
                ? JSON.stringify(option.value)
                : String(option.value)
            }
            data-testid={option.testId}
            className={`flex items-center gap-3 cursor-pointer group ${
              option.disabled ? "opacity-50 cursor-not-allowed" : ""
            } ${orientation === "horizontal" ? "flex-shrink-0" : ""}`}
          >
            <input
              id={optionId}
              name={name}
              type="radio"
              value={String(option.value)}
              checked={isSelected}
              disabled={option.disabled}
              onChange={() => !option.disabled && onChange(option.value)}
              className="sr-only peer"
            />
            <span
              className={`relative inline-flex size-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors leading-none ${
                isSelected
                  ? "border-indigo-500 bg-indigo-500"
                  : "border-slate-400 dark:border-slate-500 group-hover:border-indigo-400"
              } ${option.disabled ? "" : "group-hover:border-indigo-400"}`}
              aria-hidden="true"
            >
              {isSelected ? (
                <span className="absolute left-1/2 top-1/2 size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
              ) : null}
            </span>
            <span className="text-slate-700 dark:text-slate-300">
              {option.label}
              {option.description && (
                <div className="text-sm text-slate-500 mt-1">
                  {option.description}
                </div>
              )}
              {option.action}
            </span>
          </label>
        );
      })}
    </div>
  );
}

export type { RadioGroupProps, RadioOption };
